import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./terms.css";
import Footer from "../components/AppFooter";

export default function TermAndConditions() {
  const navigate = useNavigate();
  const [showActions, setShowActions] = useState(false);
  const consentSectionRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Always start at the top of the scroll container when entering this page
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) root.scrollTop = 0;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    // Reset showActions so the user must scroll to consent section each visit
    setShowActions(false);

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setShowActions(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5 }
    );

    if (consentSectionRef.current) {
      observer.observe(consentSectionRef.current);
    }

    return () => {
      if (consentSectionRef.current) {
        observer.unobserve(consentSectionRef.current);
      }
    };
  }, []);

  return (
    <div className="terms-root">
      <header className="terms-hero">
        <div className="terms-hero-gradient">
          <h1>Terms and Conditions</h1>
        </div>
      </header>

      <main className="terms-content">
        <div className="terms-scroll" ref={scrollContainerRef}>

          <h2 className="terms-subheader">Welcome to AuraVit</h2>
          <p className="terms-body-text">
            Welcome to AuraVit. These Terms and Conditions govern your access to and use of the AuraVit digital health platform, including its mobile and web applications, features, and services. By creating an account or using the platform, you agree to be bound by these Terms.
          </p>

          <h2 className="terms-subheader">Acceptance of Terms</h2>
          <p className="terms-body-text">
            By accessing, registering for, or using AuraVit, you acknowledge that you have read, understood, and voluntarily agreed to comply with these Terms and Conditions, along with any related policies referenced herein.
          </p>

          <p className="terms-body-text">
            These Terms constitute a legally binding agreement between you and AuraVit.
          </p>

          <h2 className="terms-subheader">Eligibility</h2>
          <p className="terms-body-text">
            AuraVit is intended for users who are legally capable of entering into a binding agreement. By using the platform, you confirm that you meet applicable age and legal requirements under relevant laws and regulations.
          </p>

          <h2 className="terms-subheader">Scope and Purpose of the Platform</h2>
          <p className="terms-body-text">
            AuraVit is a digital platform designed to support reproductive health education, health monitoring, appointment coordination, and access to community and family health services.
          </p>

          <p className="terms-body-text">
            The platform operates as an informational and service-support system and is intended to enhance access, organization, and engagement within reproductive and general health services.
          </p>

          <h2 className="terms-subheader">User Account Registration</h2>
          <p className="terms-body-text">
            To access certain features, users must create an account and provide accurate, complete, and up-to-date information. You are solely responsible for maintaining the confidentiality of your account credentials and for all activities conducted under your account.          </p>

          <p className="terms-body-text">
            AuraVit reserves the right to suspend or restrict accounts associated with false, misleading, or incomplete information.
          </p>

          <h2 className="terms-subheader">Collection and Use of Data</h2>
          <p className="terms-body-text">
            AuraVit collects and processes personal and health-related data strictly for legitimate platform functions and service delivery.
          </p>

          <div className="terms-nested-section">
            <h3 className="terms-nested-title">Types of Data Collected</h3>
            <p className="terms-nested-text">
              Data collected may include, but is not limited to:
            </p>
            <ul className="terms-nested-list">
              <li>Personal identification and contact information</li>
              <li>Health information voluntarily provided by the user</li>
              <li>Appointment records and service-related interactions</li>
              <li>System usage and engagement data</li>
            </ul>

            <h3 className="terms-nested-title">Purpose of Data Processing</h3>
            <p className="terms-nested-text">
              Collected data is used to:
            </p>
            <ul className="terms-nested-list">
              <li>Personalize user experience and platform features</li>
              <li>Enable appointment scheduling and service coordination</li>
              <li>Generate digital health records and summaries</li>
              <li>Support administrative and operational functions</li>
              <li>Improve system performance, reliability, and security</li>
            </ul>
          </div>
          
          <h2 className="terms-subheader">Data Protection and Confidentiality</h2>
          <p className="terms-body-text">
            AuraVit is committed to safeguarding user data through appropriate technical, organizational, and administrative security measures.
          </p>
          <ul className="terms-list">
            <li>All personal and health-related data is stored securely and protected against unauthorized access.</li>
            <li>Sensitive information is accessible only to authorized personnel and service providers, strictly on a need-to-know basis.</li>
            <li>AuraVit does not sell, trade, or commercially distribute personal or health data.</li>
          </ul>
          <p className="terms-body-text">
            Data may be disclosed only when:
          </p>
          <ul className="terms-list">
            <li>Explicit user consent has been granted</li>
            <li>Required by law, regulation, or lawful authority</li>
            <li>Necessary for platform operations involving authorized partners or healthcare services</li>

          </ul>

          <h2 className="terms-subheader">User Rights and Control</h2>
          <p className="terms-body-text">
            Users retain ownership and control over their personal information. Where applicable, users may:
          </p>
          <ul className="terms-list">
            <li>Review and update their information</li>
            <li>Manage privacy and notification preferences</li>
            <li>Request account deactivation in accordance with platform policies</li>
          </ul>
          <p className="terms-body-text">
            Access to user data by staff or service providers is subject to authorization, logging, and audit controls.
          </p>

          <h2 className="terms-subheader">Acceptable Use</h2>
          <p className="terms-body-text">
            Users agree to use AuraVit lawfully and responsibly. Prohibited actions include, but are not limited to:
          </p>
          <ul className="terms-list">
            <li>Providing false, inaccurate, or misleading information</li>
            <li>Attempting unauthorized access to the system or other users’ data</li>
            <li>Misusing platform features in a manner that compromises security, integrity, or functionality</li>
          </ul>
          <p className="terms-body-text">
            AuraVit reserves the right to investigate and take appropriate action against misuse.
          </p>


          <h2 className="terms-subheader">Account Suspension and Termination</h2>
          <p className="terms-body-text">
            AuraVit may suspend, restrict, or terminate user access without prior notice if:
          </p>
          <ul className="terms-list">
            <li>These Terms and Conditions are violated</li>
            <li>Platform security or integrity is compromised</li>
            <li>Use of the platform poses risk to users, partners, or operations</li>
          </ul>
          <p className="terms-body-text">
            Termination does not affect rights or obligations accrued prior to termination.
          </p>


          <h2 className="terms-subheader">Modifications to Terms</h2>
          <p className="terms-body-text">
            AuraVit reserves the right to amend or update these Terms and Conditions at any time. Updated terms will take effect upon publication within the platform.
          </p>

          <p className="terms-body-text">
            Continued use of AuraVit following any modifications constitutes acceptance of the revised Terms.
          </p>

          <h2 className="terms-subheader">Limitation of Liability</h2>
          <p className="terms-body-text">
            AuraVit shall not be held liable for losses, damages, or disruptions arising from:
          </p>
          <ul className="terms-list">
            <li>User-provided information</li>
            <li>Technical interruptions beyond reasonable control</li>
            <li>Unauthorized access resulting from user negligence</li>
          </ul>

          <p className="terms-body-text">
            Use of the platform is at the user’s discretion and responsibility.
          </p>

          <h2 className="terms-subheader">Governing Law and Jurisdiction</h2>
          <p className="terms-body-text">
            These Terms and Conditions shall be governed by and construed in accordance with applicable laws and regulations. Any disputes arising under these Terms shall be subject to the appropriate legal jurisdiction.
          </p>

          <h2 className="terms-subheader">Contact and Support</h2>
          <p className="terms-body-text">
            For inquiries, concerns, or requests related to these Terms and Conditions, users may contact AuraVit through official support channels provided within the platform.
          </p>

          <div ref={consentSectionRef}>
            <h2 className="terms-subheader">Consent</h2>
            <p className="terms-body-text">
              By proceeding with account registration, you affirm that you have read, understood, and agreed to these Terms and Conditions and consent to the collection and use of your data as described herein.
            </p>
          </div>

          <div className={`terms-actions-container ${showActions ? 'visible' : ''}`}>
            <p className="terms-actions-text">
              If you do not agree to these Terms and Conditions, you cannot proceed with registration or use the application.
            </p>
            <div className="terms-actions-buttons">
              <button className="terms-decline-btn" onClick={() => { const root = document.getElementById('root'); if (root) root.scrollTop = 0; navigate('/'); }}>
                Decline
              </button>
              <button className="terms-accept-bottom-btn" onClick={() => navigate('/signup-checkpoint')}>
                Accept
              </button>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
